import React, {forwardRef, useEffect, useState} from "react";
import {serverDirectionFor} from '@/utils/serverDirection';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {HopnPerks} from "../Components/hopnPerks.jsx";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {tripService} from "@/services/tripService";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {BOOKING_CONSTRAINTS} from "@/constants/booking";
import {isNearAirport} from "@/utils/airports";
import { tripNow, formatTripLocalTime, formatDHM, parseRideDate } from "@/utils/dateUtils.js";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {smartTimeQuestionIcon, arrowRightSlim, arrowRightWhite} from "@img/index.js";
import {ModalFooter} from "../Components/BookingModalShell.jsx";

export function TravelingTime(props) {
    const minimum_time_in_minutes = BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES;
    const [recalculate, setRecalculate] = useState(false);
    const [selectedTime, setSelectedTime] = useState(storage.get(STORAGE_KEYS.RIDE_TIME) ? storage.get(STORAGE_KEYS.RIDE_TIME) : null);
    const [selectedTimeCheck, setSelectedTimeCheck] = useState(false);
    const [quickBookingError, setQuickBookingError] = useState("");
    const [showFlightTooltip, setShowFlightTooltip] = useState(false);
    const rideDate = parseRideDate(storage.get(STORAGE_KEYS.RIDE_DATE));
    const now = tripNow();
    const maxAllowedDateTime = (() => { const d = new Date(now); d.setDate(d.getDate() + BOOKING_CONSTRAINTS.MAX_BOOKING_ADVANCE_DAYS); return d; })();
    const contactNumber = BOOKING_CONSTRAINTS.CONTACT_NUMBER;

    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS) || [];
    const firstDest = destinations[0];
    const lastDest = destinations[destinations.length - 1];
    // The server's direction (switch on) wins; otherwise our own coordinate match.
    const serverDir = serverDirectionFor(destinations[0], destinations[1])?.direction;
    const isFromAirport = serverDir ? serverDir === 'from_airport' : (firstDest?.lat && firstDest?.lng ? !!isNearAirport(firstDest.lat, firstDest.lng) : false);
    const isToAirport = serverDir ? serverDir === 'to_airport' : (lastDest?.lat && lastDest?.lng ? !!isNearAirport(lastDest.lat, lastDest.lng) : false);
    const isAirportTrip = isFromAirport || isToAirport;

    const CustomInput = forwardRef(({value, onClick}, ref) => (
        <div onClick={onClick} ref={ref} className="hopn-datepicker-input">
            {value || "Select a Time"}
        </div>
    ));

    const formatDuration = (minutes) => formatDHM(minutes);

    const gethopnMinTime = () => {
        if (!rideDate) return tripNow().setHours(0, 0, 0, 0);
        const selectedDateObj = new Date(rideDate);
        selectedDateObj.setHours(0, 0, 0, 0);
        const today = tripNow();
        today.setHours(0, 0, 0, 0);
        if (selectedDateObj.getTime() === today.getTime()) return now;
        return new Date(selectedDateObj.setHours(0, 0, 0, 0));
    };

    const gethopnMaxTime = () => {
        if (!rideDate) return new Date(tripNow().setHours(BOOKING_CONSTRAINTS.LATEST_BOOKING_HOUR, BOOKING_CONSTRAINTS.LATEST_BOOKING_MINUTE, 0, 0));
        const selectedDateObj = new Date(rideDate);
        selectedDateObj.setHours(0, 0, 0, 0);
        const maxDateObj = new Date(maxAllowedDateTime);
        maxDateObj.setHours(0, 0, 0, 0);
        if (selectedDateObj.getTime() === maxDateObj.getTime()) return maxAllowedDateTime;
        return new Date(tripNow().setHours(BOOKING_CONSTRAINTS.LATEST_BOOKING_HOUR, BOOKING_CONSTRAINTS.LATEST_BOOKING_MINUTE, 0, 0));
    };

    const isWithinMinBookingTime = (time) => {
        if (!time || !rideDate) return false;
        const selectedDateTime = new Date(rideDate);
        const timeObj = new Date(time);
        selectedDateTime.setHours(timeObj.getHours(), timeObj.getMinutes(), 0, 0);
        const minAllowed = new Date(tripNow().getTime() + minimum_time_in_minutes * 60 * 1000);
        return selectedDateTime < minAllowed;
    };

    useEffect(() => {
        if (selectedTime) {
            const storedDate = new Date(selectedTime);
            const minTime = new Date(gethopnMinTime());
            const maxTime = new Date(gethopnMaxTime());
            const storedMinutes = storedDate.getHours() * 60 + storedDate.getMinutes();
            const minMinutes = minTime.getHours() * 60 + minTime.getMinutes();
            const maxMinutes = maxTime.getHours() * 60 + maxTime.getMinutes();
            if (storedMinutes < minMinutes || storedMinutes > maxMinutes) {
                setSelectedTime(null);
                storage.remove(STORAGE_KEYS.RIDE_TIME);
            }
        }
    }, []);

    return (
        <>
            <ModalHeader
                title="Select Date and Time"
                extraClasses=""
                totalSteps={3}
                cancelButton={false}
                cancelButtonType="text"
                backStep="travelingDate"
                showBackButton={true}
                enableEditModeOnBackButton={false}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            <div className="little-title modalPages-spacing">
                <h2>Select Time</h2>
            </div>
            <div className="hopn-date-info-layout modalPages-spacing">
                <div className="inner-tab">
                    <div className={`hopn-calender-area ${selectedTimeCheck && selectedTime === null ? 'hopn-required-error' : ''}`}>
                        <div className="hopn-date-header time">
                            <DatePicker
                                open={true}
                                selected={selectedTime ? new Date(selectedTime) : null}
                                onChange={(date) => {
                                    setSelectedTime(date);
                                    storage.set(STORAGE_KEYS.RIDE_TIME, date);
                                    if (selectedTimeCheck) setSelectedTimeCheck(false);
                                    if (quickBookingError) setQuickBookingError("");
                                    if (showFlightTooltip) setShowFlightTooltip(false);
                                }}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={BOOKING_CONSTRAINTS.TIME_SLOT_INTERVAL_MINUTES}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                placeholderText="Choose a pickup time"
                                className="hopn-datepicker-input"
                                minTime={gethopnMinTime()}
                                maxTime={gethopnMaxTime()}
                                onKeyDown={(e) => e.preventDefault()}
                                customInput={<CustomInput/>}
                            />
                        </div>
                    </div>
                </div>
                <div className="inner-tab">
                    {isAirportTrip ? (
                        <div className="hopn-smart-time-card" onClick={() => props.setStep("smartTimeSelect")}>
                            <div className="hopn-smart-time-card-text gap-2">
                                <img decoding="async" src={smartTimeQuestionIcon} alt="Smart Time"/>
                                <span>Need help with pickup timing? <br/><u>Smart suggestion</u></span>
                            </div>
                            <div className="hopn-smart-time-card-arrow">
                                <img decoding="async" src={arrowRightSlim} alt="Go"/>
                            </div>
                        </div>
                    ) : (
                        <div className="hopn-calender-content">
                            <HopnPerks key="1" sliderOnly modalStyle="popup"/>
                        </div>
                    )}

                    {quickBookingError && (
                        <div className="hopn-quick-booking-error">{quickBookingError}</div>
                    )}

                    <ModalFooter>
                        <div className="hopn-submit-btn-wrapper">
                            <button
                                className={`hopn-add-passenger-button ${storage.get(STORAGE_KEYS.EDIT_STEP) === true && 'no-icon'}`}
                                disabled={recalculate}
                                onClick={async () => {
                                    setQuickBookingError("");
                                    if (showFlightTooltip) setShowFlightTooltip(false);
                                    if (selectedTime === null) {
                                        setSelectedTimeCheck(true);
                                        return;
                                    }
                                    if (isWithinMinBookingTime(selectedTime)) {
                                        const minAllowed = new Date(tripNow().getTime() + minimum_time_in_minutes * 60 * 1000);
                                        const mins = minAllowed.getMinutes();
                                        const remainder = mins % 5;
                                        if (remainder !== 0) minAllowed.setMinutes(mins + (5 - remainder), 0, 0);
                                        const earliestTime = formatTripLocalTime(minAllowed);
                                        setQuickBookingError(
                                            `For quick bookings, please contact ${contactNumber}. Online booking is only available ${formatDuration(minimum_time_in_minutes)} in advance (earliest available: ${earliestTime}).`
                                        );
                                        return;
                                    }
                                    if (isFromAirport && !storage.get(STORAGE_KEYS.FLIGHT_NUMBER)) {
                                        setShowFlightTooltip(true);
                                        return;
                                    }
                                    if (storage.get(STORAGE_KEYS.EDIT_STEP) === true) {
                                        if (tripService.needsRecalculation()) {
                                            setRecalculate(true);
                                            await tripService.recalculateCost();
                                            setRecalculate(false);
                                        }
                                        props.setStep("orderDetails");
                                    } else {
                                        if (tripService.needsRecalculation()) {
                                            storage.remove(STORAGE_KEYS.ALL_CARS_API);
                                        }
                                        props.setStep("selectedCarDetails");
                                    }
                                }}
                            >
                                {storage.get(STORAGE_KEYS.EDIT_STEP) === true ? (recalculate ? <Loader color="#fff"/> : "Done") : "Check Fare"}
                                <img decoding="async" src={arrowRightWhite} alt="+Date & Time"/>
                            </button>
                            {showFlightTooltip && (
                                <div className="hopn-submit-tooltip visible">
                                    <ModalHeader
                                        title=""
                                        extraClasses="no-space less-font no-background pt-0 pb-0 mt-0 dark"
                                        totalSteps={0}
                                        cancelButton={true}
                                        cancelButtonType="icon"
                                        ignoreEditMode={true}
                                        showBackButton={false}
                                        inline setStep={() => {}}
                                        closeModal={() => setShowFlightTooltip(false)}
                                    />
                                    <p>Add flight number from <u style={{cursor: 'pointer'}} onClick={() => props.setStep("smartTimeSelect")}>Smart suggestion</u></p>
                                </div>
                            )}
                        </div>
                    </ModalFooter>
                </div>
            </div>
        </>
    );
}
export default TravelingTime;
